import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ServiceHero from './components/ServiceHero';
import ServiceCategories from './components/ServiceCategories';
import TechnologyStack from './components/TechnologyStack';
import ProcessShowcase from './components/ProcessShowcase';
import ServiceComparison from './components/ServiceComparison';
import CTASection from './components/CTASection';

const Services = () => {
  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Services - SnapDesk | Built for Continuity. Governed for Growth.</title>
        <meta 
          name="description" 
          content="Discover SnapDesk's comprehensive digital solutions. From React 18+ development to enterprise-grade applications, we craft technology experiences that matter. Get your custom quote today." 
        />
        <meta name="keywords" content="web development, React development, digital solutions, technology consulting, UI/UX design, performance optimization" />
        <meta property="og:title" content="Services - SnapDesk | Built for Continuity. Governed for Growth." />
        <meta property="og:description" content="We don't just build technology; we architect digital experiences that matter. Explore our comprehensive service offerings." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/services" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <ServiceHero />
          
          {/* Service Categories */}
          <ServiceCategories />
          
          {/* Technology Stack */}
          <TechnologyStack />
          
          {/* Process Showcase */}
          {/* <ProcessShowcase /> */}
          
          {/* Service Comparison */}
          <ServiceComparison />
          
          {/* Call to Action */}
          <CTASection />
        </main>

        {/* Footer */}
        <footer className="bg-gradient-to-br from-primary via-secondary to-primary/80 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold">SnapDesk</h3>
                </div>
                <p className="text-gray-200 mb-4 leading-relaxed">
                  Built for Continuity. Governed for Growth.. We architect digital experiences that transcend traditional development, 
                  creating immersive journeys that feel like stepping into the future.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-gray-200 hover:text-white transition-colors duration-300">
                    <span className="sr-only">LinkedIn</span>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd"/>
                    </svg>
                  </a>
                  <a href="#" className="text-gray-200 hover:text-white transition-colors duration-300">
                    <span className="sr-only">Twitter</span>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84"/>
                    </svg>
                  </a>
                  <a href="#" className="text-gray-200 hover:text-white transition-colors duration-300">
                    <span className="sr-only">GitHub</span>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-200">
                  <li><a href="#" className="hover:text-white transition-colors duration-300">Web Development</a></li>
                  <li><a href="#" className="hover:text-white transition-colors duration-300">Mobile Apps</a></li>
                  <li><a href="#" className="hover:text-white transition-colors duration-300">UI/UX Design</a></li>
                  <li><a href="#" className="hover:text-white transition-colors duration-300">Consulting</a></li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-200">
                  <li>contact@snapdesk.com.au</li>
                  <li>+61 402 331 126</li>
                  <li>Brisbane, QLD, Australia</li>
                </ul>
              </div>
            </div>

            <div className="border-t border-white/20 mt-8 pt-8 text-center text-gray-200">
              <p>&copy; {new Date()?.getFullYear()} SnapDesk. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Services;